import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  DollarSign, Briefcase, Star,
  BarChart3, LayoutGrid, Download, FileSpreadsheet,
  Filter, Loader2, CheckSquare, Square, Search, AlertTriangle,
  Printer, CheckCircle2, XCircle, CreditCard, X, FileText, BarChart2, Percent
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from "recharts";
import { jobStatus, badgeStyle } from "@/utils/szStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fixed columns always shown first (cannot be deselected)
const FIXED_COLUMNS = ['job_id', 'job_date', 'owner_name', 'crew_name'];

// Column categories for the report builder
const COLUMN_CATEGORIES = {
  'Job Details': ['row_type', 'job_title', 'date_posted', 'job_type', 'location', 'job_status', 'hired', 'num_applicants', 'data_source'],
  'People': ['owner_email', 'crew_email', 'crew_role'],
  'Financial': ['job_total', 'platform_fee', 'business_total_charged', 'crew_payment', 'job_total_inc_extra', 'crew_payment_inc_extra', 'shootzu_revenue'],
  'Payments': ['payment_type', 'invoice_status', 'first_payment_date', 'first_payment_amount', 'first_payment_stripe_id', 'second_payment_date', 'second_payment_amount', 'second_payment_stripe_id', 'extra_payment_date', 'extra_payment_amount', 'extra_payment_reason', 'extra_payment_stripe_id'],
  'Tax / GST': ['crew_gst_registered', 'gst_on_job_total', 'gst_on_platform_fee'],
  'Cancellation': ['cancellation_reason', 'cancellation_notes', 'cancelled_at', 'cancelled_by', 'cancellation_type', 'cancelled_crew_name', 'cancelled_crew_email'],
  'Cancellation Fees': ['cancellation_invoice_number', 'cancellation_fee_base_amount', 'cancellation_fee_ex_gst', 'cancellation_fee_gst', 'cancellation_fee_inc_gst', 'cancellation_fee_status', 'cancellation_fee_paid_at'],
  'Refunds': ['refund_amount', 'refund_type', 'refund_status'],
};

const COLUMN_LABELS = {
  row_type: 'Row Type',
  job_id: 'Job ID', job_title: 'Job Title', job_date: 'Job Date', date_posted: 'Date Posted',
  job_type: 'Job Type', location: 'Location', job_status: 'Job Status', hired: 'Hired',
  num_applicants: 'Applicants', owner_name: 'Owner Name', owner_email: 'Owner Email',
  crew_name: 'Crew Name', crew_email: 'Crew Email', crew_role: 'Crew Role',
  job_total: 'Job Total', platform_fee: 'Platform Fee (5%)', business_total_charged: 'Business Charged',
  crew_payment: 'Crew Payment (98%)',
  job_total_inc_extra: 'Total of Job Incl. Additional Payment',
  crew_payment_inc_extra: 'Total Paid to Crew Incl. Additional Payment',
  shootzu_revenue: 'Shootzu Revenue',
  payment_type: 'Payment Type', invoice_status: 'Invoice Status',
  first_payment_date: '1st Payment Date', first_payment_amount: '1st Payment Amount',
  first_payment_stripe_id: '1st Payment Stripe ID', second_payment_date: '2nd Payment Date',
  second_payment_amount: '2nd Payment Amount', second_payment_stripe_id: '2nd Payment Stripe ID',
  extra_payment_date: 'Extra Payment Date', extra_payment_amount: 'Extra Payment Amount',
  extra_payment_reason: 'Extra Payment Reason', extra_payment_stripe_id: 'Extra Payment Stripe ID',
  crew_gst_registered: 'Crew GST Registered', gst_on_job_total: 'GST on Job Total',
  data_source: 'Data Source',
  gst_on_platform_fee: 'GST on Platform Fee',
  cancellation_reason: 'Cancellation Reason', cancellation_notes: 'Cancellation Notes',
  cancelled_at: 'Cancelled At', cancelled_by: 'Cancelled By',
  cancellation_type: 'Cancellation Type', cancelled_crew_name: 'Cancelled Crew',
  cancelled_crew_email: 'Cancelled Crew Email',
  cancellation_invoice_number: 'Cancellation Invoice #',
  cancellation_fee_base_amount: 'Cancellation Base Amount',
  cancellation_fee_ex_gst: 'Cancellation Fee (ex GST)',
  cancellation_fee_gst: 'GST on Cancellation Fee',
  cancellation_fee_inc_gst: 'Cancellation Fee (inc GST)',
  cancellation_fee_status: 'Cancellation Fee Status',
  cancellation_fee_paid_at: 'Cancellation Fee Paid Date',
  refund_amount: 'Refund Amount', refund_type: 'Refund Type', refund_status: 'Refund Status',
};

const ALL_COLUMNS = Object.values(COLUMN_CATEGORIES).flat();

const ROW_TYPES = ['Invoice', 'Cancellation Fee', 'Refund', 'Pending Manual Refund'];

const JOB_STATUSES = [
  'SEEKING_APPLICANTS', 'OFFER_SENT', 'HIRED', 'PENDING_COMPLETION',
  'RELEASE_PAYMENT', 'PAID_COMPLETED', 'CANCELLED'
];

const PAYMENT_STATUSES = ['payment_pending', 'paid', 'completed', 'cancelled', 'refunded'];

// Generate financial year options
const generateFYOptions = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const options = [];
  const startYear = currentMonth >= 7 ? currentYear : currentYear - 1;
  for (let i = startYear; i >= startYear - 4; i--) {
    options.push(`${i}-${i + 1}`);
  }
  return options;
};

// Chart palette (TOKENS chart-1..5)
const CHART = {
  violet: '#572B7C', aqua: '#28C0D0', green: '#15803D', amber: '#D97706', blue: '#2563EB',
};

const RANGE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'month', label: '30d' },
  { value: 'quarter', label: '90d' },
  { value: 'year', label: '1y' },
];

const TAB_DEFS = [
  { value: 'dashboard', label: 'Dashboard', Icon: LayoutGrid },
  { value: 'export', label: 'Export Reports', Icon: Download },
  { value: 'builder', label: 'Report Builder', Icon: Filter },
  { value: 'search', label: 'Search', Icon: Search },
];

// Rank tile colors for ranked boards
const rankTile = (i) => (
  i === 0 ? { bg: '#FEF3C7', fg: '#B45309' }
  : i === 1 ? { bg: '#EEF0F4', fg: '#2B3142' }
  : i === 2 ? { bg: '#FDE8D7', fg: '#9A5B2E' }
  : { bg: '#F5F6F9', fg: '#8A90A2' }
);

const AdminReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('quarter');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [reportData, setReportData] = useState({
    financial: {},
    jobs: {},
    users: {},
    geographic: {},
    performance: {}
  });

  // Export Reports state
  const [exportLoading, setExportLoading] = useState(null); // 'full' | 'bas' | null
  const [fullReportStart, setFullReportStart] = useState('');
  const [fullReportEnd, setFullReportEnd] = useState('');
  const [basYear, setBasYear] = useState(generateFYOptions()[0]);
  const [basQuarter, setBasQuarter] = useState('');
  const [gstYear, setGstYear] = useState(generateFYOptions()[0]);
  const [gstQuarter, setGstQuarter] = useState('');

  // Report Builder state
  const [builderLoading, setBuilderLoading] = useState(false);
  const [builderStart, setBuilderStart] = useState('');
  const [builderEnd, setBuilderEnd] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([...ALL_COLUMNS]);

  // Ensure fixed columns are always included and prepended in the right order
  const getOrderedColumns = (cols) => {
    const extra = cols.filter(c => !FIXED_COLUMNS.includes(c));
    return [...FIXED_COLUMNS, ...extra];
  };
  const [statusFilters, setStatusFilters] = useState([]);
  const [paymentStatusFilters, setPaymentStatusFilters] = useState([]);
  const [hasExtraFilter, setHasExtraFilter] = useState(null); // null = any, true, false
  const [rowTypeFilters, setRowTypeFilters] = useState([]); // empty = all
  const [pendingRefunds, setPendingRefunds] = useState([]);
  const [pendingRefundsLoading, setPendingRefundsLoading] = useState(false);
  const [markingRefundId, setMarkingRefundId] = useState(null);

  const fetchPendingRefunds = async () => {
    setPendingRefundsLoading(true);
    try {
      const token = localStorage.getItem('shootzu_admin_token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/job-cancellations/pending-refunds`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setPendingRefunds(res.data?.items || []);
    } catch (e) {
      console.error('Failed to fetch pending refunds:', e);
    } finally {
      setPendingRefundsLoading(false);
    }
  };

  const markRefundCompleted = async (cancellationId) => {
    const note = window.prompt('Optional Stripe refund ID or note for audit:');
    setMarkingRefundId(cancellationId);
    try {
      const token = localStorage.getItem('shootzu_admin_token');
      await axios.post(
        `${BACKEND_URL}/api/admin/job-cancellations/${cancellationId}/mark-refunded`,
        { stripe_refund_id: note || '' },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      await fetchPendingRefunds();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to mark refund completed');
    } finally {
      setMarkingRefundId(null);
    }
  };
  const [searchJobQuery, setSearchJobQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [savedTemplates, setSavedTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem('shootzu_report_templates') || '[]'); } catch { return []; }
  });
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  const saveTemplate = () => {
    if (!templateName.trim()) return;
    const template = {
      name: templateName.trim(),
      columns: selectedColumns,
      statusFilters,
      paymentStatusFilters,
      hasExtraFilter,
      rowTypeFilters,
      createdAt: new Date().toISOString(),
    };
    const updated = [...savedTemplates.filter(t => t.name !== template.name), template];
    setSavedTemplates(updated);
    localStorage.setItem('shootzu_report_templates', JSON.stringify(updated));
    setTemplateName('');
    setShowSaveTemplate(false);
  };

  const loadTemplate = (template) => {
    setSelectedColumns(template.columns || [...ALL_COLUMNS]);
    setStatusFilters(template.statusFilters || []);
    setPaymentStatusFilters(template.paymentStatusFilters || []);
    setHasExtraFilter(template.hasExtraFilter ?? null);
    setRowTypeFilters(template.rowTypeFilters || []);
  };

  const handleSearchJob = async () => {
    if (!searchJobQuery.trim()) return;
    setSearchLoading(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const token = localStorage.getItem('shootzu_admin_token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/reports/search`, {
        params: { query: searchJobQuery.trim() },
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.data.success) {
        setSearchResult(res.data);
      } else {
        setSearchError(res.data.message || 'Job not found');
      }
    } catch (err) {
      setSearchError(err.response?.data?.detail || 'Failed to search');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchJobQuery('');
    setSearchResult(null);
    setSearchError('');
  };

  const fmtMoney = (n) => `$${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const deleteTemplate = (name) => {
    const updated = savedTemplates.filter(t => t.name !== name);
    setSavedTemplates(updated);
    localStorage.setItem('shootzu_report_templates', JSON.stringify(updated));
  };

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin/dashboard');
  }, [navigate]);

  useEffect(() => {
    fetchAllReports();
    fetchPendingRefunds();
  }, [dateRange]);

  const fetchAllReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('shootzu_admin_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/reports/dashboard?range=${dateRange}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 60000 }
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      if (error.code === 'ECONNABORTED') {
        alert('Report data took too long to load. Try a smaller date range.');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async (url, method, data, filename, loadingKey) => {
    try {
      setExportLoading(loadingKey);
      setBuilderLoading(loadingKey === 'custom');
      const token = localStorage.getItem('shootzu_admin_token');
      const config = {
        url: `${BACKEND_URL}${url}`,
        method,
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      };
      if (method === 'GET' && data) config.params = data;
      if (method === 'POST') config.data = data;

      const response = await axios(config);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download report. Please try again.');
    } finally {
      setExportLoading(null);
      setBuilderLoading(false);
    }
  };

  const handleFullReport = () => {
    const params = {};
    if (fullReportStart) params.start_date = fullReportStart;
    if (fullReportEnd) params.end_date = fullReportEnd;
    const today = new Date().toISOString().split('T')[0];
    downloadCSV('/api/admin/reports/export/full-financial', 'GET', params, `shootzu_full_report_${today}.csv`, 'full');
  };

  const handleBASReport = () => {
    const params = { financial_year: basYear };
    if (basQuarter) params.quarter = basQuarter;
    const qLabel = basQuarter || 'full_year';
    downloadCSV('/api/admin/reports/export/bas', 'GET', params, `shootzu_bas_FY${basYear}_${qLabel}.csv`, 'bas');
  };

  const handleShootzuGSTReport = () => {
    const params = { financial_year: gstYear };
    if (gstQuarter) params.quarter = gstQuarter;
    const qLabel = gstQuarter || 'full_year';
    downloadCSV('/api/admin/reports/export/shootzu-gst', 'GET', params, `shootzu_gst_report_FY${gstYear}_${qLabel}.csv`, 'gst');
  };

  const handleCustomReport = () => {
    const body = {
      columns: getOrderedColumns(selectedColumns),
      filters: {},
    };
    if (builderStart) body.start_date = builderStart;
    if (builderEnd) body.end_date = builderEnd;
    if (statusFilters.length > 0) body.filters.status = statusFilters;
    if (paymentStatusFilters.length > 0) body.filters.payment_status = paymentStatusFilters;
    if (hasExtraFilter !== null) body.filters.has_extra_payment = hasExtraFilter;
    if (rowTypeFilters.length > 0) body.filters.row_type = rowTypeFilters;

    const today = new Date().toISOString().split('T')[0];
    downloadCSV('/api/admin/reports/export/custom', 'POST', body, `shootzu_custom_report_${today}.csv`, 'custom');
  };

  const toggleColumn = (col) => {
    // Fixed columns cannot be deselected
    if (FIXED_COLUMNS.includes(col)) return;
    setSelectedColumns(prev =>
      prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
    );
  };

  const toggleAllInCategory = (category) => {
    const cols = COLUMN_CATEGORIES[category];
    const allSelected = cols.every(c => selectedColumns.includes(c));
    if (allSelected) {
      setSelectedColumns(prev => prev.filter(c => !cols.includes(c)));
    } else {
      setSelectedColumns(prev => [...new Set([...prev, ...cols])]);
    }
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const togglePaymentStatusFilter = (status) => {
    setPaymentStatusFilters(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '70vh' }}>
        <div className="text-center">
          <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin" style={{ color: CHART.aqua }} />
          <p className="text-[13px]" style={{ color: '#8A90A2' }}>Loading analytics…</p>
        </div>
      </div>
    );
  }

  const { financial = {}, jobs = {}, users = {}, geographic = {}, performance = {}, cancellations = {} } = reportData;
  const displayedMonths = financial.monthly_revenue?.slice(-12) || [];
  const displayMax = displayedMonths.length > 0 ? Math.max(...displayedMonths.map(m => m.amount)) : 0;
  const mobileDisplayedMonths = financial.monthly_revenue?.slice(-6) || [];
  const mobileDisplayMax = mobileDisplayedMonths.length > 0 ? Math.max(...mobileDisplayedMonths.map(m => m.amount)) : 0;

  // ---- Derived chart/board data (fed from the SAME fetched data) ----
  const fmt0 = (n) => `$${(Number(n) || 0).toLocaleString()}`;
  const revChartData = displayedMonths.map((m) => ({
    label: (m.month || '').split(' ')[0],
    full: m.month,
    amount: Number(m.amount) || 0,
  }));
  const jobsByState = geographic.jobs_by_state || [];
  const jobsStateMax = jobsByState.length ? Math.max(...jobsByState.map(s => s.count || 0)) : 0;
  const revByState = geographic.revenue_by_state || [];
  const revStateMax = revByState.length ? Math.max(...revByState.map(s => s.revenue || 0)) : 0;

  const kpis = [
    { label: 'Total Revenue', value: fmt0(financial.total_revenue), sub: 'Job payments received', fg: CHART.green },
    { label: 'Platform Fees', value: fmt0(financial.total_fees), sub: '7% total (5% + 2%)', fg: '#0C1120' },
    { label: 'Total Crew Payments', value: fmt0(financial.total_crew_payments), sub: 'Paid to crew (98%)', fg: '#0C1120' },
    { label: 'Crew Liability Held', value: fmt0(financial.crew_liability_held?.amount), sub: `Owed to crew · ${financial.crew_liability_held?.jobs || 0} active bookings`, fg: CHART.blue },
    { label: 'Avg Job Value', value: `$${Math.round(financial.avg_job_value || 0).toLocaleString()}`, sub: 'Per completed job', fg: '#0C1120' },
    { label: 'Total Jobs', value: (jobs.total_jobs || 0).toLocaleString(), sub: `${jobs.completed_jobs || 0} completed`, fg: '#0C1120' },
    { label: 'Jobs Value Posted', value: fmt0(financial.total_jobs_value), sub: `Across ${(jobs.total_jobs || 0).toLocaleString()} jobs`, fg: '#0C1120' },
  ];

  return (
    <>
      {/* Sticky page header */}
      <header
        className="sz-header"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}
      >
        <div>
          <h1 className="sz-h1" style={{ margin: 0 }}>Reports &amp; Analytics</h1>
          <div style={{ fontSize: 12.5, color: '#8A90A2', marginTop: 3 }}>Financial and operational reporting</div>
        </div>
        {/* Date-range segmented control */}
        <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid #E6E9EF', padding: 4, borderRadius: 11 }}>
          {RANGE_OPTIONS.map((r) => {
            const on = dateRange === r.value;
            return (
              <button
                key={r.value}
                onClick={() => setDateRange(r.value)}
                style={{
                  padding: '7px 13px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'Work Sans', fontSize: 12.5, fontWeight: 600,
                  color: on ? '#fff' : '#8A90A2', background: on ? '#0C1120' : 'transparent',
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Tab nav */}
      <div style={{ padding: '16px 30px 0' }}>
        <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid #E6E9EF', padding: 4, borderRadius: 12, width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }} className="szscroll">
          {TAB_DEFS.map((t) => {
            const on = activeTab === t.value;
            const Icon = t.Icon;
            return (
              <button
                key={t.value}
                onClick={() => setActiveTab(t.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', border: 'none',
                  borderRadius: 9, cursor: 'pointer', fontFamily: 'Work Sans', fontSize: 13, fontWeight: 600,
                  color: on ? '#fff' : '#8A90A2', background: on ? '#572B7C' : 'transparent', whiteSpace: 'nowrap',
                }}
              >
                <Icon size={15} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scroll body */}
      <main className="sz-fade" style={{ padding: '22px 30px 56px', maxWidth: 1320 }}>
            {/* ========== DASHBOARD TAB ========== */}
            {activeTab === 'dashboard' && (
            <div>
              {/* KPI cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(195px,1fr))', gap: 13 }}>
                {kpis.map((k) => (
                  <div key={k.label} className="sz-card sz-card-hover" style={{ padding: 16 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>{k.label}</div>
                    <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7, color: k.fg }}>{k.value}</div>
                    <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* Pending Manual Refunds — admin work queue */}
              {pendingRefunds.length > 0 && (
                <div className="sz-card" style={{ marginTop: 16, padding: '18px 20px', borderColor: '#FBBF77', borderWidth: 2, background: 'rgba(255,237,213,.35)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertTriangle size={18} color="#C2410C" />
                    <h3 className="sz-h3" style={{ margin: 0, color: '#9A3412' }}>Pending Manual Refunds ({pendingRefunds.length})</h3>
                  </div>
                  <p style={{ fontSize: 12, color: '#8A90A2', margin: '8px 0 14px' }}>
                    Each entry below is a no-show cancellation awaiting a manual Stripe refund.
                    After refunding the hirer via the Stripe dashboard, click "Mark refunded" to record it in the financial reports.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {pendingRefunds.map((pr) => (
                      <div key={pr.cancellation_id} className="flex flex-col md:flex-row md:items-center justify-between gap-2" style={{ padding: 12, background: '#fff', borderRadius: 12, border: '1px solid #FDE0BE' }}>
                        <div className="min-w-0 flex-1">
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#0C1120' }} className="truncate">
                            {pr.job_title || pr.job_id}
                            <span className="ml-2" style={{ fontSize: 11.5, fontWeight: 400, color: '#8A90A2' }}>{pr.job_number}</span>
                          </p>
                          <p className="truncate" style={{ fontSize: 12, color: '#8A90A2' }}>
                            {pr.cancelled_crew_name} ({pr.cancelled_crew_role || 'crew'}) — {pr.cancellation_reason || 'no reason'}
                          </p>
                          <p className="sz-mono" style={{ fontSize: 10.5, color: '#A8AEBC', marginTop: 2 }}>
                            PI: {pr.pending_refund_payment_intent_id || '—'} · Cancelled {pr.cancellation_timestamp?.split('T')[0]} by {pr.cancelled_by}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="sz-stat-num" style={{ fontSize: 16, color: '#C2410C' }}>
                            ${(pr.pending_refund_amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <button
                            className="sz-btn sz-btn-outline"
                            style={{ padding: '7px 13px', borderColor: '#FBBF77', color: '#9A3412' }}
                            disabled={markingRefundId === pr.cancellation_id}
                            onClick={() => markRefundCompleted(pr.cancellation_id)}
                          >
                            {markingRefundId === pr.cancellation_id ? 'Marking…' : 'Mark refunded'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Revenue by Month — Recharts bar with gradient bars */}
              <div className="sz-card" style={{ marginTop: 16, padding: '20px 22px' }}>
                <h3 className="sz-h3" style={{ margin: '0 0 4px' }}>Revenue by Month</h3>
                <div style={{ fontSize: 12, color: '#8A90A2' }}>Platform revenue across the last 12 months</div>
                {revChartData.length > 0 ? (
                  <div style={{ width: '100%', height: 260, marginTop: 14 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={revChartData} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="rgrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={CHART.violet} />
                            <stop offset="100%" stopColor={CHART.aqua} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke="#F1F3F7" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#A8AEBC', fontFamily: 'Work Sans' }} />
                        <YAxis tickLine={false} axisLine={false} width={48} tick={{ fontSize: 10.5, fill: '#A8AEBC', fontFamily: 'Work Sans' }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                        <Tooltip
                          cursor={{ fill: 'rgba(87,43,124,.06)' }}
                          contentStyle={{ borderRadius: 11, border: '1px solid #E6E9EF', fontSize: 12, fontFamily: 'Work Sans' }}
                          labelFormatter={(_, p) => (p && p[0] ? p[0].payload.full : '')}
                          formatter={(v) => [`$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Revenue']}
                        />
                        <Bar dataKey="amount" fill="url(#rgrad)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center" style={{ height: 200, color: '#A8AEBC', flexDirection: 'column', gap: 8 }}>
                    <BarChart3 className="opacity-40" size={40} />
                    <p style={{ fontSize: 13 }}>No revenue data available</p>
                  </div>
                )}
              </div>

              {/* Jobs by State + Revenue by State */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }} className="max-lg:!grid-cols-1">
                <div className="sz-card" style={{ padding: '20px 22px' }}>
                  <h3 className="sz-h3" style={{ margin: '0 0 14px' }}>Jobs by State</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {jobsByState.map((s) => (
                      <div key={s.state} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ width: 40, fontSize: 12, fontWeight: 600, color: '#2B3142' }}>{s.state}</span>
                        <div style={{ flex: 1, height: 8, background: '#F1F3F7', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${jobsStateMax > 0 ? Math.round((s.count / jobsStateMax) * 100) : 0}%`, background: CHART.blue, borderRadius: 999 }} />
                        </div>
                        <span className="sz-stat-num" style={{ width: 34, textAlign: 'right', fontSize: 12 }}>{s.count}</span>
                      </div>
                    ))}
                    {jobsByState.length === 0 && <p style={{ fontSize: 12.5, color: '#A8AEBC' }}>No data.</p>}
                  </div>
                </div>
                <div className="sz-card" style={{ padding: '20px 22px' }}>
                  <h3 className="sz-h3" style={{ margin: '0 0 14px' }}>Revenue by State</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {revByState.map((s) => (
                      <div key={s.state} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ width: 40, fontSize: 12, fontWeight: 600, color: '#2B3142' }}>{s.state}</span>
                        <div style={{ flex: 1, height: 8, background: '#F1F3F7', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${revStateMax > 0 ? Math.round((s.revenue / revStateMax) * 100) : 0}%`, background: CHART.green, borderRadius: 999 }} />
                        </div>
                        <span className="sz-stat-num" style={{ width: 64, textAlign: 'right', fontSize: 12 }}>${Math.round((s.revenue || 0) / 1000)}k</span>
                      </div>
                    ))}
                    {revByState.length === 0 && <p style={{ fontSize: 12.5, color: '#A8AEBC' }}>No data.</p>}
                  </div>
                </div>
              </div>

              {/* Ranked boards: Top Crew / Top Businesses / Highest Rated */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(290px,1fr))', gap: 16, marginTop: 16 }}>
                {/* Top Crew */}
                <div className="sz-card" style={{ padding: '20px 22px' }}>
                  <h3 className="sz-h3" style={{ margin: '0 0 12px' }}>Top Crew</h3>
                  {(performance.top_crew || []).map((crew, idx) => {
                    const t = rankTile(idx);
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 2px', borderTop: '1px solid #F1F3F7' }}>
                        <span style={{ width: 22, height: 22, borderRadius: 7, background: t.bg, color: t.fg, fontFamily: 'Poppins', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{idx + 1}</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{crew.name}</span>
                        <span style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 13, color: '#0F766E' }}>{crew.jobs} jobs</span>
                      </div>
                    );
                  })}
                  {(performance.top_crew || []).length === 0 && <p style={{ fontSize: 12.5, color: '#A8AEBC' }}>No data.</p>}
                </div>

                {/* Top Businesses */}
                <div className="sz-card" style={{ padding: '20px 22px' }}>
                  <h3 className="sz-h3" style={{ margin: '0 0 12px' }}>Top Businesses</h3>
                  {(performance.top_businesses || []).map((business, idx) => {
                    const t = rankTile(idx);
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 2px', borderTop: '1px solid #F1F3F7' }}>
                        <span style={{ width: 22, height: 22, borderRadius: 7, background: t.bg, color: t.fg, fontFamily: 'Poppins', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{idx + 1}</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{business.name}</span>
                        <span style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 13, color: CHART.blue }}>${(business.total_spent || 0).toLocaleString()}</span>
                      </div>
                    );
                  })}
                  {(performance.top_businesses || []).length === 0 && <p style={{ fontSize: 12.5, color: '#A8AEBC' }}>No data.</p>}
                </div>

                {/* Highest Rated */}
                <div className="sz-card" style={{ padding: '20px 22px' }}>
                  <h3 className="sz-h3" style={{ margin: '0 0 12px' }}>Highest Rated</h3>
                  {(performance.highest_rated || []).map((user, idx) => {
                    const t = rankTile(idx);
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 2px', borderTop: '1px solid #F1F3F7' }}>
                        <span style={{ width: 22, height: 22, borderRadius: 7, background: t.bg, color: t.fg, fontFamily: 'Poppins', fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{idx + 1}</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'Poppins', fontWeight: 700, fontSize: 13, color: CHART.amber }}>
                          {user.rating} <Star size={13} color={CHART.amber} fill={CHART.amber} />
                          <span style={{ fontSize: 11, fontWeight: 400, color: '#A8AEBC' }}>({user.reviews})</span>
                        </span>
                      </div>
                    );
                  })}
                  {(performance.highest_rated || []).length === 0 && <p style={{ fontSize: 12.5, color: '#A8AEBC' }}>No data.</p>}
                </div>
              </div>

              {/* Cancellation Fees & Refunds */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 13, marginTop: 16 }}>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Cancellation Fees Collected</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7, color: CHART.amber }}>${(cancellations.fees_collected_inc_gst || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>inc GST · ex GST ${(cancellations.fees_collected_ex_gst || 0).toLocaleString()}</div>
                </div>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Outstanding Fees</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7, color: '#B91C1C' }}>${(cancellations.fees_outstanding_inc_gst || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>{cancellations.fees_outstanding_count || 0} unpaid invoice{(cancellations.fees_outstanding_count || 0) === 1 ? '' : 's'} (all-time)</div>
                </div>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Refunds Issued</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7, color: '#0E7490' }}>${(cancellations.refunds_issued_amount || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>Money refunded to hirers in period</div>
                </div>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Pending Manual Refunds</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7, color: '#C2410C' }}>${(cancellations.refunds_pending_amount || 0).toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>Awaiting admin to refund via Stripe</div>
                </div>
              </div>

              {/* Job status distribution + Job types & revenue */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(290px,1fr))', gap: 16, marginTop: 16 }}>
                <div className="sz-card" style={{ padding: '20px 22px' }}>
                  <h3 className="sz-h3" style={{ margin: '0 0 14px' }}>Job Status Distribution</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(jobs.by_status || []).map((status) => {
                      const cfg = jobStatus(status.name);
                      const pct = jobs.total_jobs > 0 ? (status.count / jobs.total_jobs) * 100 : 0;
                      return (
                        <div key={status.name} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span className="sz-badge" style={badgeStyle(cfg)}>{cfg.label}</span>
                          <div style={{ flex: 1, height: 8, background: '#F1F3F7', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: cfg.fg, borderRadius: 999 }} />
                          </div>
                          <span className="sz-stat-num" style={{ width: 40, textAlign: 'right', fontSize: 12 }}>{status.count}</span>
                        </div>
                      );
                    })}
                    {(jobs.by_status || []).length === 0 && <p style={{ fontSize: 12.5, color: '#A8AEBC' }}>No data.</p>}
                  </div>
                </div>
                <div className="sz-card" style={{ padding: '20px 22px' }}>
                  <h3 className="sz-h3" style={{ margin: '0 0 14px' }}>Job Types &amp; Revenue</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(jobs.by_type || []).map((type) => (
                      <div key={type.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>{type.name.replace('_', ' ')}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span className="sz-pill-count" style={{ background: '#EDE4F4', color: '#572B7C' }}>{type.count}</span>
                          <span style={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: 13, color: CHART.green }}>${(type.total_revenue || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                    {(jobs.by_type || []).length === 0 && <p style={{ fontSize: 12.5, color: '#A8AEBC' }}>No data.</p>}
                  </div>
                </div>
              </div>

              {/* User analytics + Performance metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(195px,1fr))', gap: 13, marginTop: 16 }}>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Total Users</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7 }}>{users.total_users || 0}</div>
                  <div style={{ fontSize: 11, color: CHART.green, marginTop: 3 }}>+ {users.new_this_month || 0} this month</div>
                </div>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Businesses</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7 }}>{users.active_businesses || 0}</div>
                  <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>Active job posters</div>
                </div>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Total Invoices</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7 }}>{(jobs.completed_jobs || 0) + (financial.with_extras || 0)}</div>
                  <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>Paid transactions</div>
                </div>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Completion Rate</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7 }}>{performance.completion_rate || 0}%</div>
                  <div style={{ height: 6, background: '#F1F3F7', borderRadius: 999, marginTop: 8, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${performance.completion_rate || 0}%`, background: CHART.green, borderRadius: 999 }} />
                  </div>
                </div>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Avg Rating</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {performance.avg_rating || 0} <Star size={18} color={CHART.amber} fill={CHART.amber} />
                  </div>
                  <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>{performance.total_reviews || 0} reviews</div>
                </div>
                <div className="sz-card sz-card-hover" style={{ padding: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2' }}>Hire Rate</div>
                  <div className="sz-stat-num" style={{ fontSize: 22, marginTop: 7 }}>{performance.hire_rate || 0}%</div>
                  <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 3 }}>Applications to Hire</div>
                </div>
              </div>

              {/* Payment structure analysis */}
              <div className="sz-card" style={{ padding: '20px 22px', marginTop: 16 }}>
                <h3 className="sz-h3" style={{ margin: '0 0 14px' }}>Payment Structure Analysis</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 13 }}>
                  <div style={{ padding: 14, background: '#F5F6F9', borderRadius: 12, border: '1px solid #E6E9EF' }}>
                    <div style={{ fontSize: 12, color: '#8A90A2', marginBottom: 4 }}>Full Upfront</div>
                    <div className="sz-stat-num" style={{ fontSize: 20, color: CHART.green }}>{financial.full_payments || 0}</div>
                    <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 2 }}>${(financial.full_payment_total || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ padding: 14, background: '#F5F6F9', borderRadius: 12, border: '1px solid #E6E9EF' }}>
                    <div style={{ fontSize: 12, color: '#8A90A2', marginBottom: 4 }}>Split Payments</div>
                    <div className="sz-stat-num" style={{ fontSize: 20, color: CHART.blue }}>{financial.split_payments || 0}</div>
                    <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 2 }}>${(financial.split_payment_total || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ padding: 14, background: '#F5F6F9', borderRadius: 12, border: '1px solid #E6E9EF' }}>
                    <div style={{ fontSize: 12, color: '#8A90A2', marginBottom: 4 }}>With Additional</div>
                    <div className="sz-stat-num" style={{ fontSize: 20, color: '#572B7C' }}>{financial.with_extras || 0}</div>
                    <div style={{ fontSize: 11, color: '#8A90A2', marginTop: 2 }}>${(financial.extras_total || 0).toLocaleString()} extras</div>
                  </div>
                </div>
              </div>

              {/* Most common skills */}
              {(users.top_skills || []).length > 0 && (
                <div className="sz-card" style={{ padding: '20px 22px', marginTop: 16 }}>
                  <h3 className="sz-h3" style={{ margin: '0 0 14px' }}>Most Common Skills</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(users.top_skills || []).map((skill) => (
                      <span key={skill.name} className="sz-pill" style={{ cursor: 'default', textTransform: 'capitalize' }}>
                        {skill.name.replace('_', ' ')}
                        <span className="sz-pill-count" style={{ background: '#DBEAFE', color: '#2563EB' }}>{skill.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* ========== EXPORT REPORTS TAB ========== */}
            {activeTab === 'export' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(330px,1fr))', gap: 16 }}>
              {/* Full Financial Report */}
              <div className="sz-card" style={{ padding: 22, display: 'flex', flexDirection: 'column' }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: '#EDE4F4', color: '#78287C', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <FileText size={21} />
                </div>
                <h3 className="sz-h3" style={{ fontSize: 15.5, margin: '0 0 6px' }}>Full Job &amp; Financial Report</h3>
                <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#8A90A2', margin: '0 0 14px' }}>
                  Every job with budgets, fees, payouts and payment status across a chosen date range — incl. Stripe IDs, crew info and GST breakdowns.
                </p>
                <div style={{ marginBottom: 14 }}>
                  <div className="sz-overline" style={{ color: '#8A90A2', marginBottom: 7 }}>Date Range (optional)</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="date" value={fullReportStart} onChange={(e) => setFullReportStart(e.target.value)} className="sz-input" style={{ padding: '9px 11px' }} />
                    <input type="date" value={fullReportEnd} onChange={(e) => setFullReportEnd(e.target.value)} className="sz-input" style={{ padding: '9px 11px' }} />
                  </div>
                </div>
                <button onClick={handleFullReport} disabled={exportLoading === 'full'} className="sz-btn sz-btn-primary" style={{ width: '100%', marginTop: 'auto' }}>
                  {exportLoading === 'full' ? (<><Loader2 size={15} className="animate-spin" /> Generating…</>) : (<><Download size={15} /> Generate &amp; Download</>)}
                </button>
              </div>

              {/* BAS / Tax Report */}
              <div className="sz-card" style={{ padding: 22, display: 'flex', flexDirection: 'column' }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: '#DBEAFE', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <BarChart2 size={21} />
                </div>
                <h3 className="sz-h3" style={{ fontSize: 15.5, margin: '0 0 6px' }}>BAS / Tax Report</h3>
                <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#8A90A2', margin: '0 0 14px' }}>
                  GST collected and paid, structured by financial year and quarter for BAS lodgement.
                </p>
                <div style={{ marginBottom: 12 }}>
                  <div className="sz-overline" style={{ color: '#8A90A2', marginBottom: 7 }}>Financial Year</div>
                  <select value={basYear} onChange={(e) => setBasYear(e.target.value)} className="sz-input">
                    {generateFYOptions().map(fy => (<option key={fy} value={fy}>FY {fy}</option>))}
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div className="sz-overline" style={{ color: '#8A90A2', marginBottom: 7 }}>Quarter</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {[
                      { value: '', label: 'Full Year' },
                      { value: 'Q1', label: 'Q1' }, { value: 'Q2', label: 'Q2' },
                      { value: 'Q3', label: 'Q3' }, { value: 'Q4', label: 'Q4' },
                    ].map(q => (
                      <button key={q.value} onClick={() => setBasQuarter(q.value)} className={`sz-pill${basQuarter === q.value ? ' sz-active' : ''}`} style={{ padding: '6px 12px', fontSize: 12 }}>
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={handleBASReport} disabled={exportLoading === 'bas'} className="sz-btn sz-btn-primary" style={{ width: '100%', marginTop: 'auto' }}>
                  {exportLoading === 'bas' ? (<><Loader2 size={15} className="animate-spin" /> Generating…</>) : (<><Download size={15} /> Generate &amp; Download</>)}
                </button>
              </div>

              {/* Shootzu GST Report */}
              <div className="sz-card" style={{ padding: 22, display: 'flex', flexDirection: 'column' }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: '#DCFCE7', color: '#15803D', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Percent size={21} />
                </div>
                <h3 className="sz-h3" style={{ fontSize: 15.5, margin: '0 0 6px' }}>Shootzu GST Report</h3>
                <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#8A90A2', margin: '0 0 14px' }}>
                  Platform-side GST summary on fees and commissions (7% per accepted job), by FY and quarter. Cancelled jobs excluded.
                </p>
                <div style={{ marginBottom: 12 }}>
                  <div className="sz-overline" style={{ color: '#8A90A2', marginBottom: 7 }}>Financial Year</div>
                  <select value={gstYear} onChange={(e) => setGstYear(e.target.value)} className="sz-input">
                    {generateFYOptions().map(fy => (<option key={fy} value={fy}>FY {fy}</option>))}
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div className="sz-overline" style={{ color: '#8A90A2', marginBottom: 7 }}>Quarter</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {[
                      { value: '', label: 'Full Year' },
                      { value: 'Q1', label: 'Q1' }, { value: 'Q2', label: 'Q2' },
                      { value: 'Q3', label: 'Q3' }, { value: 'Q4', label: 'Q4' },
                    ].map(q => (
                      <button key={q.value} onClick={() => setGstQuarter(q.value)} className={`sz-pill${gstQuarter === q.value ? ' sz-active' : ''}`} style={{ padding: '6px 12px', fontSize: 12 }}>
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={handleShootzuGSTReport} disabled={exportLoading === 'gst'} className="sz-btn sz-btn-primary" style={{ width: '100%', marginTop: 'auto' }}>
                  {exportLoading === 'gst' ? (<><Loader2 size={15} className="animate-spin" /> Generating…</>) : (<><Download size={15} /> Generate &amp; Download</>)}
                </button>
              </div>
            </div>
            )}

            {/* ========== REPORT BUILDER TAB ========== */}
            {activeTab === 'builder' && (
            <div className="sz-card" style={{ padding: 22 }}>
              <h3 className="sz-h3" style={{ fontSize: 16, margin: '0 0 4px' }}>Custom Report Builder</h3>
              <p style={{ fontSize: 12.5, color: '#8A90A2', margin: '0 0 18px' }}>
                Select columns to include, choose a date range and filters, then download or save as a reusable template.
              </p>

              {/* Date range */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#572B7C', textTransform: 'uppercase', marginBottom: 9 }}>Date Range</div>
                <div className="flex flex-col sm:flex-row" style={{ gap: 10, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#8A90A2', marginBottom: 4, display: 'block' }}>From</label>
                    <input type="date" value={builderStart} onChange={(e) => setBuilderStart(e.target.value)} className="sz-input" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#8A90A2', marginBottom: 4, display: 'block' }}>To</label>
                    <input type="date" value={builderEnd} onChange={(e) => setBuilderEnd(e.target.value)} className="sz-input" />
                  </div>
                  <button onClick={() => { setBuilderStart(''); setBuilderEnd(''); }} className="sz-btn sz-btn-outline">Clear</button>
                </div>
              </div>

              {/* Columns */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#572B7C', textTransform: 'uppercase' }}>
                  Columns ({selectedColumns.length} of {ALL_COLUMNS.length})
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setSelectedColumns([...ALL_COLUMNS])} className="sz-btn sz-btn-outline" style={{ padding: '6px 12px', fontSize: 12 }}>
                    <CheckSquare size={13} /> Select All
                  </button>
                  <button onClick={() => setSelectedColumns([...FIXED_COLUMNS])} className="sz-btn sz-btn-outline" style={{ padding: '6px 12px', fontSize: 12 }}>
                    <Square size={13} /> Clear All
                  </button>
                </div>
              </div>

              {/* Always-included fixed columns */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#0E7490', textTransform: 'uppercase', marginBottom: 9 }}>
                  Always Included (first 4 columns)
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {FIXED_COLUMNS.map(col => (
                    <span key={col} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, color: '#0E7490', background: '#CFFAFE', border: '1px solid #A5E9F2', padding: '7px 12px', borderRadius: 999 }}>
                      <CheckCircle2 size={13} /> {COLUMN_LABELS[col]}
                    </span>
                  ))}
                </div>
              </div>

              {/* Column groups — toggle chips */}
              {Object.entries(COLUMN_CATEGORIES).map(([category, cols]) => {
                const selectedCount = cols.filter(c => selectedColumns.includes(c)).length;
                const allSelected = selectedCount === cols.length;
                return (
                  <div key={category} style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#572B7C', textTransform: 'uppercase' }}>{category}</span>
                      <span style={{ fontSize: 11, color: '#8A90A2' }}>({selectedCount}/{cols.length})</span>
                      <button onClick={() => toggleAllInCategory(category)} style={{ fontSize: 11, fontWeight: 600, color: '#28C0D0', background: 'none', border: 'none', cursor: 'pointer' }}>
                        {allSelected ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {cols.map(col => {
                        const on = selectedColumns.includes(col);
                        return (
                          <button
                            key={col}
                            onClick={() => toggleColumn(col)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500,
                              cursor: 'pointer', padding: '7px 12px', borderRadius: 999,
                              color: on ? '#572B7C' : '#8A90A2',
                              background: on ? '#EDE4F4' : '#F5F6F9',
                              border: `1px solid ${on ? '#D9C5E6' : '#E6E9EF'}`,
                            }}
                          >
                            {COLUMN_LABELS[col]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Filters */}
              <div style={{ paddingTop: 18, borderTop: '1px solid #EEF0F4' }}>
                {/* Row Type */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#572B7C', textTransform: 'uppercase', marginBottom: 9 }}>Row Type</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {ROW_TYPES.map(rt => (
                      <button key={rt} onClick={() => setRowTypeFilters(prev => prev.includes(rt) ? prev.filter(x => x !== rt) : [...prev, rt])} className={`sz-pill${rowTypeFilters.includes(rt) ? ' sz-active' : ''}`}>
                        {rt}
                      </button>
                    ))}
                    {rowTypeFilters.length > 0 && (
                      <button onClick={() => setRowTypeFilters([])} className="sz-pill" style={{ color: '#DC2626', borderColor: '#FCA5A5' }}>Clear</button>
                    )}
                  </div>
                  {rowTypeFilters.length === 0 && <p style={{ fontSize: 11, color: '#A8AEBC', marginTop: 6 }}>No filter applied (Invoice + Cancellation Fee + Refund + Pending all included)</p>}
                </div>

                {/* Job Status */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#572B7C', textTransform: 'uppercase', marginBottom: 9 }}>Job Status</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {JOB_STATUSES.map(status => (
                      <button key={status} onClick={() => toggleStatusFilter(status)} className={`sz-pill${statusFilters.includes(status) ? ' sz-active' : ''}`}>
                        {jobStatus(status).label}
                      </button>
                    ))}
                    {statusFilters.length > 0 && (
                      <button onClick={() => setStatusFilters([])} className="sz-pill" style={{ color: '#DC2626', borderColor: '#FCA5A5' }}>Clear</button>
                    )}
                  </div>
                  {statusFilters.length === 0 && <p style={{ fontSize: 11, color: '#A8AEBC', marginTop: 6 }}>No filter applied (all statuses included)</p>}
                </div>

                {/* Payment Status */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#572B7C', textTransform: 'uppercase', marginBottom: 9 }}>Invoice / Payment Status</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {PAYMENT_STATUSES.map(status => (
                      <button key={status} onClick={() => togglePaymentStatusFilter(status)} className={`sz-pill${paymentStatusFilters.includes(status) ? ' sz-active' : ''}`} style={{ textTransform: 'capitalize' }}>
                        {status.replace(/_/g, ' ')}
                      </button>
                    ))}
                    {paymentStatusFilters.length > 0 && (
                      <button onClick={() => setPaymentStatusFilters([])} className="sz-pill" style={{ color: '#DC2626', borderColor: '#FCA5A5' }}>Clear</button>
                    )}
                  </div>
                  {paymentStatusFilters.length === 0 && <p style={{ fontSize: 11, color: '#A8AEBC', marginTop: 6 }}>No filter applied (all statuses included)</p>}
                </div>

                {/* Extra Payments */}
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#572B7C', textTransform: 'uppercase', marginBottom: 9 }}>Extra Payments</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {[
                      { value: null, label: 'Any' },
                      { value: true, label: 'Has Extra Payment' },
                      { value: false, label: 'No Extra Payment' },
                    ].map(opt => (
                      <button key={String(opt.value)} onClick={() => setHasExtraFilter(opt.value)} className={`sz-pill${hasExtraFilter === opt.value ? ' sz-active' : ''}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Saved templates */}
              {savedTemplates.length > 0 && (
                <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #EEF0F4' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'Poppins', letterSpacing: '.3px', color: '#572B7C', textTransform: 'uppercase', marginBottom: 9 }}>Saved Templates</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {savedTemplates.map((t) => (
                      <div key={t.name} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#EDE4F4', border: '1px solid #D9C5E6', borderRadius: 999, padding: '6px 12px' }}>
                        <button onClick={() => loadTemplate(t)} style={{ fontSize: 12.5, fontWeight: 600, color: '#572B7C', background: 'none', border: 'none', cursor: 'pointer' }}>{t.name}</button>
                        <button onClick={() => deleteTemplate(t.name)} style={{ color: '#A8AEBC', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Save template row + download */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20, paddingTop: 18, borderTop: '1px solid #EEF0F4' }}>
                <button onClick={() => setShowSaveTemplate(true)} className="sz-btn sz-btn-outline">
                  <FileSpreadsheet size={15} /> Save Template
                </button>
                <button
                  onClick={handleCustomReport}
                  disabled={builderLoading || selectedColumns.length === 0}
                  className="sz-btn sz-btn-primary"
                  style={{ marginLeft: 'auto' }}
                >
                  {builderLoading ? (<><Loader2 size={15} className="animate-spin" /> Generating Report…</>) : (<><Download size={15} /> Download Custom Report (CSV)</>)}
                </button>
              </div>
            </div>
            )}

            {/* ========== SEARCH TAB ========== */}
            {activeTab === 'search' && (
            <div className="print:space-y-3">
              {/* Search Input */}
              <div className="sz-card print:hidden" style={{ padding: 22, maxWidth: 720 }}>
                <h3 className="sz-h3" style={{ fontSize: 16, margin: '0 0 14px' }}>Single Job Search</h3>
                <div className="flex" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                    <Search size={17} color="#A8AEBC" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                      type="text"
                      value={searchJobQuery}
                      onChange={(e) => setSearchJobQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchJob()}
                      placeholder="Enter a job ID, e.g. JOB-04821 or 3177…"
                      className="sz-input"
                      style={{ paddingLeft: 40, padding: '12px 14px 12px 40px' }}
                    />
                  </div>
                  <button onClick={handleSearchJob} disabled={searchLoading || !searchJobQuery.trim()} className="sz-btn sz-btn-primary" style={{ padding: '11px 22px' }}>
                    {searchLoading ? <Loader2 size={15} className="animate-spin" /> : 'Search'}
                  </button>
                  {(searchResult || searchError) && (
                    <button onClick={handleClearSearch} className="sz-btn sz-btn-outline">
                      <X size={15} /> Clear
                    </button>
                  )}
                </div>
                <p style={{ fontSize: 11.5, color: '#A8AEBC', marginTop: 10 }}>Search a job ID to pull every financial record for that job, verified live against Stripe.</p>
                {searchError && <p style={{ color: '#DC2626', fontSize: 13, marginTop: 12 }}>{searchError}</p>}
                {!searchResult && !searchError && !searchLoading && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8A90A2', fontSize: 13 }}>
                    Search a job ID to view its full financial record.
                  </div>
                )}
              </div>

              {/* Searching modal — live Stripe verification can take a few seconds */}
              {searchLoading && (
                <div className="sz-modal-backdrop print:hidden">
                  <div className="sz-modal-card" style={{ padding: '40px 32px', alignItems: 'center', gap: 16, maxWidth: 380, width: '100%' }}>
                    <Loader2 size={44} className="animate-spin" style={{ color: '#572B7C' }} />
                    <p className="sz-h3" style={{ margin: 0 }}>Searching job records…</p>
                    <p style={{ fontSize: 12, color: '#8A90A2', textAlign: 'center', margin: 0 }}>Verifying transactions with Stripe — this may take a few seconds</p>
                  </div>
                </div>
              )}

              {/* Search Results — full financial snapshot for one job */}
              {searchResult && (
                <div id="job-financial-snapshot">
                  {/* Header */}
                  <div className="sz-card" style={{ padding: '18px 20px', marginTop: 16 }}>
                    <div className="flex items-start justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
                      <h3 className="sz-h3" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Briefcase size={18} color="#2563EB" />
                        {searchResult.job.title}
                        {(() => { const cfg = jobStatus(searchResult.job.status); return <span className="sz-badge" style={badgeStyle(cfg)}>{cfg.label}</span>; })()}
                      </h3>
                      <button onClick={() => window.print()} className="sz-btn sz-btn-outline print:hidden" style={{ padding: '7px 13px' }}>
                        <Printer size={15} /> Print / Save PDF
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: 16, marginTop: 14, fontSize: 13 }}>
                      <div><span style={{ color: '#8A90A2', display: 'block', fontSize: 11 }}>Job ID</span><span style={{ fontWeight: 600 }}>{searchResult.job.job_number}</span></div>
                      <div><span style={{ color: '#8A90A2', display: 'block', fontSize: 11 }}>Posted On</span><span style={{ fontWeight: 600 }}>{searchResult.job.created_at || 'N/A'}</span></div>
                      <div><span style={{ color: '#8A90A2', display: 'block', fontSize: 11 }}>Job Date</span><span style={{ fontWeight: 600 }}>{searchResult.job.dates?.length > 1 ? searchResult.job.dates.join(', ') : (searchResult.job.date || 'N/A')}</span></div>
                      <div><span style={{ color: '#8A90A2', display: 'block', fontSize: 11 }}>Location</span><span style={{ fontWeight: 600 }}>{searchResult.job.location || 'N/A'}</span></div>
                    </div>
                  </div>

                  {/* Stripe verification warnings */}
                  {searchResult.stripe_warnings?.length > 0 && (
                    <div style={{ background: '#FFEDD5', border: '1px solid #FED7AA', borderRadius: 12, padding: 12, marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <AlertTriangle size={16} color="#C2410C" style={{ marginTop: 2, flexShrink: 0 }} />
                      <div style={{ fontSize: 12, color: '#9A3412' }}>
                        <p style={{ fontWeight: 600, marginBottom: 4 }}>Some charges could not be verified with Stripe — figures below may be incomplete:</p>
                        <ul className="list-disc list-inside" style={{ margin: 0 }}>
                          {searchResult.stripe_warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Parties */}
                  <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 16, marginTop: 16 }}>
                    <div className="sz-card" style={{ padding: '18px 20px' }}>
                      <h3 className="sz-h3" style={{ fontSize: 13.5, color: '#2563EB', margin: '0 0 10px' }}>Job Posted By</h3>
                      <p style={{ fontWeight: 600, fontSize: 13 }}>{searchResult.owner.name}</p>
                      <p style={{ fontSize: 12, color: '#8A90A2' }}>{searchResult.owner.email}</p>
                      <div className="flex" style={{ gap: 12, marginTop: 8, fontSize: 11.5, flexWrap: 'wrap' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 6, ...(searchResult.owner.gst_registered ? { background: '#DCFCE7', color: '#15803D' } : { background: '#EEF0F4', color: '#8A90A2' }) }}>
                          GST: {searchResult.owner.gst_registered ? 'Registered' : 'Not registered'}
                        </span>
                        {searchResult.owner.abn && <span style={{ color: '#A8AEBC' }}>ABN: {searchResult.owner.abn}</span>}
                      </div>
                    </div>
                    <div className="sz-card" style={{ padding: '18px 20px' }}>
                      <h3 className="sz-h3" style={{ fontSize: 13.5, color: '#0F766E', margin: '0 0 10px' }}>Hired Crew ({searchResult.crew.length})</h3>
                      {searchResult.crew.length === 0 ? (
                        <p style={{ fontSize: 12, color: '#A8AEBC' }}>No crew hired</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {searchResult.crew.map((c, i) => (
                            <div key={i} style={{ borderBottom: i < searchResult.crew.length - 1 ? '1px solid #F1F3F7' : 'none', paddingBottom: 8 }}>
                              <div className="flex items-start justify-between">
                                <div>
                                  <p style={{ fontWeight: 600, fontSize: 13 }}>{c.name} <span style={{ fontSize: 11.5, color: '#572B7C', fontWeight: 400 }}>({c.role})</span></p>
                                  <p style={{ fontSize: 12, color: '#8A90A2' }}>{c.email}</p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <p style={{ fontSize: 13, fontWeight: 600, color: '#0F766E' }}>{fmtMoney(c.payout ?? (c.subtotal || 0))}</p>
                                  <p style={{ fontSize: 10, color: '#A8AEBC' }}>payout</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2" style={{ gap: '4px 12px', marginTop: 6, fontSize: 11.5 }}>
                                <div><span style={{ color: '#A8AEBC' }}>Hired:</span> <span style={{ fontWeight: 500 }}>{c.hired_at || '—'}</span></div>
                                <div><span style={{ color: '#A8AEBC' }}>Paid:</span> <span style={{ fontWeight: 500 }}>{c.paid_date || (c.payment_released ? 'Released' : 'Pending')}</span></div>
                                <div>
                                  <span style={{ padding: '1px 6px', borderRadius: 6, ...(c.gst_registered ? { background: '#DCFCE7', color: '#15803D' } : { background: '#EEF0F4', color: '#8A90A2' }) }}>GST: {c.gst_registered ? 'Yes' : 'No'}</span>
                                </div>
                                {c.abn && <div style={{ color: '#A8AEBC' }}>ABN: {c.abn}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payments timeline — live from Stripe */}
                  <div className="sz-card" style={{ padding: '18px 20px', marginTop: 16 }}>
                    <h3 className="sz-h3" style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CreditCard size={18} color="#4338CA" />
                      Payments Charged ({searchResult.payments?.length || 0})
                      <span style={{ fontSize: 11.5, fontWeight: 400, color: '#A8AEBC' }}>verified live with Stripe</span>
                    </h3>
                    {(searchResult.payments?.length || 0) === 0 ? (
                      <p style={{ fontSize: 12, color: '#A8AEBC' }}>No Stripe charges found for this job.</p>
                    ) : (
                      <div className="overflow-x-auto szscroll">
                        <table className="w-full" style={{ fontSize: 13 }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #F1F3F7', textAlign: 'left', fontSize: 11, color: '#8A90A2' }}>
                              <th style={{ paddingBottom: 8, paddingRight: 16 }}>Date</th>
                              <th style={{ paddingBottom: 8, paddingRight: 16 }}>Payment</th>
                              <th style={{ paddingBottom: 8, paddingRight: 16, textAlign: 'right' }}>Charged</th>
                              <th style={{ paddingBottom: 8, paddingRight: 16, textAlign: 'right' }}>Stripe Fee</th>
                              <th style={{ paddingBottom: 8, paddingRight: 16, textAlign: 'right' }}>Net</th>
                              <th style={{ paddingBottom: 8, paddingRight: 16 }}>Stripe ID</th>
                              <th style={{ paddingBottom: 8, textAlign: 'center' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {searchResult.payments.map((p, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #F1F3F7' }}>
                                <td style={{ padding: '8px 16px 8px 0', whiteSpace: 'nowrap' }}>{p.date ? new Date(p.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                                <td style={{ padding: '8px 16px 8px 0' }}>{p.label}</td>
                                <td style={{ padding: '8px 16px 8px 0', textAlign: 'right', fontWeight: 600 }}>{p.gross != null ? fmtMoney(p.gross) : '—'}</td>
                                <td style={{ padding: '8px 16px 8px 0', textAlign: 'right', color: '#DC2626' }}>{p.stripe_fee != null ? `-${fmtMoney(p.stripe_fee)}` : '—'}</td>
                                <td style={{ padding: '8px 16px 8px 0', textAlign: 'right', fontWeight: 500 }}>{p.net != null ? fmtMoney(p.net) : '—'}</td>
                                <td className="sz-mono" style={{ padding: '8px 16px 8px 0', fontSize: 11, color: '#8A90A2' }}>{p.charge_id || '—'}</td>
                                <td style={{ padding: '8px 0', textAlign: 'center' }}>
                                  {p.verified
                                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#15803D', fontSize: 11.5 }}><CheckCircle2 size={14} /> Verified</span>
                                    : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#DC2626', fontSize: 11.5 }}><XCircle size={14} /> Unverified</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* GST — both sides, clearly separated */}
                  <div className="sz-card" style={{ padding: '18px 20px', marginTop: 16 }}>
                    <h3 className="sz-h3" style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileSpreadsheet size={18} color="#0F766E" />
                      GST Breakdown
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 16 }}>
                      <div style={{ borderRadius: 12, border: '1px solid #CCFBF1', background: 'rgba(204,251,241,.4)', padding: 12 }}>
                        <p className="sz-overline" style={{ color: '#0F766E', marginBottom: 8 }}>Shootzu fee (always GST)</p>
                        <div className="flex justify-between" style={{ fontSize: 13 }}><span style={{ color: '#8A90A2' }}>GST on platform fee</span><span style={{ fontWeight: 600 }}>{fmtMoney(searchResult.gst?.shootzu_fee_gst)}</span></div>
                      </div>
                      <div style={{ borderRadius: 12, border: '1px solid #E6E9EF', padding: 12 }}>
                        <p className="sz-overline" style={{ color: '#2B3142', marginBottom: 8 }}>
                          Crew / job amount {searchResult.gst?.crew_gst_registered ? <span style={{ color: '#15803D', textTransform: 'none' }}>(crew GST-registered)</span> : <span style={{ color: '#A8AEBC', textTransform: 'none' }}>(crew not GST-registered → no GST)</span>}
                        </p>
                        <div className="flex justify-between" style={{ fontSize: 13 }}><span style={{ color: '#8A90A2' }}>GST on job amount</span><span style={{ fontWeight: 600 }}>{fmtMoney(searchResult.gst?.job_amount_gst)}</span></div>
                        <div className="flex justify-between" style={{ fontSize: 13, marginTop: 4 }}><span style={{ color: '#8A90A2' }}>GST on extra payments</span><span style={{ fontWeight: 600 }}>{fmtMoney(searchResult.gst?.extra_payment_gst)}</span></div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #F1F3F7' }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>Total GST charged</span>
                      <span className="sz-stat-num" style={{ fontSize: 18, color: '#0F766E' }}>{fmtMoney(searchResult.gst?.total_gst_charged)}</span>
                    </div>
                  </div>

                  {/* Totals & revenue */}
                  <div className="sz-card" style={{ padding: '18px 20px', marginTop: 16 }}>
                    <h3 className="sz-h3" style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DollarSign size={18} color="#15803D" />
                      Totals &amp; Revenue
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5" style={{ gap: 13 }}>
                      <div style={{ background: '#F5F6F9', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: '#8A90A2' }}>Total Charged</p>
                        <p className="sz-stat-num" style={{ fontSize: 18 }}>{fmtMoney(searchResult.totals?.charged)}</p>
                      </div>
                      <div style={{ background: '#FEE2E2', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: '#8A90A2' }}>Stripe Fees</p>
                        <p className="sz-stat-num" style={{ fontSize: 18, color: '#B91C1C' }}>-{fmtMoney(searchResult.totals?.stripe_fees)}</p>
                      </div>
                      <div style={{ background: '#CCFBF1', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: '#8A90A2' }}>GST Collected</p>
                        <p className="sz-stat-num" style={{ fontSize: 18, color: '#0F766E' }}>{fmtMoney(searchResult.totals?.gst_collected)}</p>
                      </div>
                      <div style={{ background: '#DCFCE7', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: '#8A90A2' }}>Crew Payout</p>
                        <p className="sz-stat-num" style={{ fontSize: 18, color: '#15803D' }}>{fmtMoney(searchResult.totals?.crew_payout)}</p>
                      </div>
                      <div style={{ background: '#EDE4F4', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: '#8A90A2' }}>Shootzu Net Revenue</p>
                        <p className="sz-stat-num" style={{ fontSize: 18, color: '#572B7C' }}>{fmtMoney(searchResult.totals?.shootzu_net_revenue)}</p>
                        <p style={{ fontSize: 10, color: '#A8AEBC', marginTop: 2 }}>after Stripe fees</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}
      </main>

      {/* Save Template modal */}
      {showSaveTemplate && (
        <div className="sz-modal-backdrop" onClick={() => setShowSaveTemplate(false)}>
          <div className="sz-modal-card" style={{ width: 420, maxWidth: '100%' }} onClick={(e) => e.stopPropagation()}>
            <div className="sz-modal-header-grad" style={{ padding: '18px 22px', borderTopLeftRadius: 19, borderTopRightRadius: 19 }}>
              <h3 className="sz-h3" style={{ margin: 0, color: '#fff' }}>Save Report Template</h3>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,.8)', margin: '4px 0 0' }}>Save the current columns &amp; filters for reuse.</p>
            </div>
            <div style={{ padding: 22 }}>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: '#8A90A2', marginBottom: 6, display: 'block' }}>Template name</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name…"
                className="sz-input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveTemplate()}
              />
              <div className="flex justify-end" style={{ gap: 10, marginTop: 18 }}>
                <button onClick={() => setShowSaveTemplate(false)} className="sz-btn sz-btn-outline">Cancel</button>
                <button onClick={saveTemplate} disabled={!templateName.trim()} className="sz-btn sz-btn-primary" style={{ background: '#572B7C' }}>Save Template</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminReports;
